/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Link } from 'react-router';

import { Tag, Typography2 } from '@coursera/cds-core';

import { useSideNavCollapse } from 'bundles/enterprise-admin-navigation/components/side-nav/base/SideNavCollapseProvider';
import { SideNavToolTipWrapper } from 'bundles/enterprise-admin-navigation/components/side-nav/base/SideNavToolTipWrapper';
import type { SideNavItem as SideNavItemType } from 'bundles/enterprise-admin-navigation/components/side-nav/base/types';

import _t from 'i18n!nls/enterprise-admin-navigation';

const styles = {
  item: css`
    padding: 0;
    margin: 0;
    list-style: none;
  `,
  button: css`
    width: 100%;
    height: 36px;
    display: flex;
    align-items: center;
    padding: 0 var(--cds-spacing-100);
    background-color: transparent;
    border: none;
    border-radius: var(--cds-border-radius-100);
    cursor: pointer;
    transition:
      background-color 200ms ease,
      color 200ms ease,
      font-weight 200ms ease,
      width 200ms ease,
      padding 200ms ease;

    /* && doubles the class to beat the global .styleguide a:hover { text-decoration: underline } rule */
    &&,
    &&:link,
    &&:visited,
    &&:hover,
    &&:focus,
    &&:active {
      text-decoration: none;
      color: var(--cds-color-neutral-primary);

      & svg {
        color: var(--cds-color-neutral-primary-weak);
      }
    }

    /* :focus-visible (not :focus) so the hover background doesn't stick after a mouse click */
    &&:hover,
    &&:focus-visible {
      background-color: var(--cds-color-emphasis-primary-background-xweak);
    }

    &&[aria-current='page'],
    &&[aria-current='page']:link,
    &&[aria-current='page']:visited,
    &&[aria-current='page']:hover,
    &&[aria-current='page']:focus,
    &&[aria-current='page']:active {
      color: var(--cds-color-neutral-primary);
      background-color: var(--cds-color-emphasis-primary-background-xweak);

      & svg {
        color: var(--cds-color-neutral-primary);
      }
    }
  `,
  buttonCollapsed: css`
    width: 36px;
    justify-content: center;
    margin-left: var(--cds-spacing-50);
  `,
  icon: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--cds-spacing-300);
    height: var(--cds-spacing-300);
    flex-shrink: 0;
  `,
  label: css`
    /* Size to content (don't grow) so a trailing badge hugs the label instead
       of being pushed to the far edge; min-width:0 keeps ellipsis working. */
    flex: 0 1 auto;
    min-width: 0;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition:
      opacity 200ms ease,
      margin 200ms ease;
    opacity: 1;
    margin-left: var(--cds-spacing-150);
  `,
  labelHidden: css`
    opacity: 0;
    margin-left: 0;
  `,
  newTag: css`
    margin-left: var(--cds-spacing-100);
    flex-shrink: 0;
  `,
};

type Props = {
  item: SideNavItemType;
};

/**
 *
 * The SideNavItem component is used to render a nav item in the sidebar.
 * @param item - The item to render
 * @returns The SideNavItem component
 */
export const SideNavItem = ({ item }: Props) => {
  const { isSideNavCollapsed } = useSideNavCollapse();
  const { label, icon: Icon, href, onClick, isActive, showNewBadge } = item;

  if (!href && !onClick) {
    return null;
  }

  const buttonContent = (
    <>
      <SideNavToolTipWrapper content={label} isSideNavCollapsed={isSideNavCollapsed}>
        <span css={styles.icon}>
          <Icon size="medium" />
        </span>
      </SideNavToolTipWrapper>
      <Typography2
        variant={isActive ? 'actionSecondary' : 'bodySecondary'}
        component="span"
        color="inherit"
        css={[styles.label, isSideNavCollapsed && styles.labelHidden]}
      >
        {label}
      </Typography2>
      {/* Collapsed rail only shows icons, so the badge is hidden there. */}
      {showNewBadge && !isSideNavCollapsed ? (
        <Tag variant="status" priority="primary" color="highlight1" css={styles.newTag}>
          {_t('New')}
        </Tag>
      ) : null}
    </>
  );

  const buttonStyles = [styles.button, isSideNavCollapsed && styles.buttonCollapsed];

  if (href) {
    return (
      <li css={styles.item}>
        <Link
          to={href}
          css={buttonStyles}
          aria-current={isActive ? 'page' : undefined}
          aria-label={isSideNavCollapsed ? label : undefined}
        >
          {buttonContent}
        </Link>
      </li>
    );
  }

  return (
    <li css={styles.item}>
      <button
        type="button"
        onClick={onClick}
        css={buttonStyles}
        aria-current={isActive ? 'page' : undefined}
        aria-label={isSideNavCollapsed ? label : undefined}
      >
        {buttonContent}
      </button>
    </li>
  );
};

export default SideNavItem;
